import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Card,
  Typography,
  Avatar,
  IconButton,
  Button,
  Stack,
  TextField,
  Select,
  MenuItem,
  Dialog,
  DialogContent,
  FormControl,
  OutlinedInput,
  Chip,
  Divider,
  CircularProgress,
  useTheme,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import WifiOutlinedIcon from "@mui/icons-material/WifiOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LiveTvOutlinedIcon from "@mui/icons-material/LiveTvOutlined";
import { tokens } from "../../theme";
import { CalendarIcon, LocationIcon } from "../../components/icons";
import apiService from "../../service/Api";

type ApplicationStatus =
  | "draft"
  | "submitted"
  | "under_review"
  | "approved"
  | "rejected";

interface ApiOrganiser {
  lead_name: string;
  org_name: string;
  email: string | null;
  phone_number: string | null;
  experience: string;
}

interface ApiAudience {
  slug: string;
  label: string;
}

interface ApiApplication {
  id: number;
  reference_id: string;
  status: ApplicationStatus;
  created_at: string;
  organiser: ApiOrganiser;
  conference_title: string;
  conference_date: string;
  conference_time: string | null;
  attendees: number | null;
  theme: string;
  local_impact: string;
  venue_name: string;
  venue_latitude: string | null;
  venue_longitude: string | null;
  capacity: number | null;
  event_flyer: string | null;
  audiences: (string | ApiAudience)[];
  amenities: (string | ApiAudience)[];
}

const amenityLabels: Record<string, { label: string; icon: React.ReactNode }> =
  {
    internet: {
      label: "High-Speed Internet",
      icon: <WifiOutlinedIcon sx={{ fontSize: 16 }} />,
    },
    power: {
      label: "Power Continuity",
      icon: <BoltOutlinedIcon sx={{ fontSize: 16 }} />,
    },
    catering: {
      label: "On-Site Production Space",
      icon: <VideocamOutlinedIcon sx={{ fontSize: 16 }} />,
    },
  };

const getStatusStyles = (status: ApplicationStatus) => {
  switch (status) {
    case "submitted":
      return { bgcolor: "#F79E1B22", color: "#F79E1B" };
    case "approved":
      return { bgcolor: "#4BA66322", color: "#4BA663" };
    case "under_review":
      return { bgcolor: "#3B82F622", color: "#3B82F6" };
    case "rejected":
      return { bgcolor: "#EB433522", color: "#EB4335" };
    default:
      return { bgcolor: "#E5E7EB", color: "#6B7280" };
  }
};

const STATUS_LABELS: Record<ApplicationStatus, string> = {
  draft: "Draft",
  submitted: "Submitted",
  under_review: "In Review",
  approved: "Approved",
  rejected: "Rejected",
};

const getInitials = (name: string) =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

const declineReasons = [
  {
    label: "Not enough reach",
    message:
      "Thank you for applying. Unfortunately your event does not meet our minimum broadcast reach requirements at this time.",
  },
  {
    label: "Infrastructure gap",
    message:
      "Thank you for your application. We are unable to proceed as the venue does not meet our broadcast infrastructure requirements.",
  },
  {
    label: "Scheduling conflict",
    message:
      "Thank you for applying. Unfortunately we are unable to accommodate your event date within our current production schedule.",
  },
];

const modalContent: Record<
  ApplicationStatus,
  { title: string; subtitle: string }
> = {
  rejected: {
    title: "Decline Broadcast Application",
    subtitle:
      "Select a reason template and customise the message sent to the applicant.",
  },
  under_review: {
    title: "Request Additional Information",
    subtitle:
      "Send a WhatsApp message requesting more details about the event or venue.",
  },
  approved: {
    title: "Approve Broadcast Application",
    subtitle:
      "Confirm that this event will receive sponsored livestream coverage.",
  },
  submitted: { title: "", subtitle: "" },
  draft: { title: "", subtitle: "" },
};

export default function StreamFundApplicationDetail() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { id: referenceId } = useParams<{ id: string }>();

  const cardBg = colors.primary[900];
  const mutedCard = colors.primary[800];
  const textPrimary = colors.primary[700];
  const textMuted = colors.primary[300];

  const [apiApp, setApiApp] = useState<ApiApplication | null>(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<ApplicationStatus>("submitted");
  const [notes, setNotes] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [editNotes, setEditNotes] = useState(false);
  const [editInternalNotes, setEditInternalNotes] = useState(false);

  const [openModal, setOpenModal] = useState(false);
  const [selectedAction, setSelectedAction] =
    useState<ApplicationStatus | null>(null);
  const [reason, setReason] = useState(declineReasons[0].label);
  const [message, setMessage] = useState("");
  const [savingStatus, setSavingStatus] = useState(false);

  useEffect(() => {
    if (!referenceId) return;
    setLoading(true);
    apiService
      .get<ApiApplication>(`/api/streamfund/applications/${referenceId}/`)
      .then((res) => {
        setApiApp(res.data);
        setStatus(res.data.status);
      })
      .catch(() => setApiApp(null))
      .finally(() => setLoading(false));
  }, [referenceId]);

  const handleStatusChange = (value: ApplicationStatus) => {
    if (value === "submitted" || value === "draft") return;
    setSelectedAction(value);

    if (value === "rejected") {
      setReason(declineReasons[0].label);
      setMessage(declineReasons[0].message);
    } else if (value === "under_review") {
      setMessage(
        "Hello, could you please provide additional details about your event venue and expected online viewership?",
      );
    } else if (value === "approved") {
      setMessage(
        "Congratulations! Your application for sponsored livestream coverage has been approved. Our production team will be in touch shortly.",
      );
    }

    setOpenModal(true);
  };

  const handleConfirm = async () => {
    if (!selectedAction || !referenceId) return;
    setSavingStatus(true);
    try {
      const res = await apiService.patch<ApiApplication>(
        `/api/streamfund/applications/${referenceId}/`,
        { status: selectedAction },
      );
      setApiApp(res.data);
      setStatus(res.data.status);
      setOpenModal(false);
    } finally {
      setSavingStatus(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!apiApp) {
    return (
      <Box sx={{ py: 6, textAlign: "center" }}>
        <Typography sx={{ color: textMuted, fontSize: 14 }}>
          Application not found.
        </Typography>
      </Box>
    );
  }

  const audienceLabels = (apiApp.audiences ?? []).map((a) =>
    typeof a === "string" ? a : a.label,
  );
  const amenitySlugs = (apiApp.amenities ?? []).map((a) =>
    typeof a === "string" ? a : a.slug,
  );

  const app = {
    id: apiApp.reference_id,
    status: apiApp.status,
    submitted: apiApp.created_at,
    organiser: {
      leadName: apiApp.organiser.lead_name,
      orgName: apiApp.organiser.org_name || "—",
      email: apiApp.organiser.email || "—",
      phoneNumber: apiApp.organiser.phone_number || "—",
      experience: apiApp.organiser.experience || "Not provided",
    },
    conference: {
      title: apiApp.conference_title,
      theme: apiApp.theme || "Not provided",
      date: apiApp.conference_date,
      attendees: apiApp.attendees ?? 0,
      localImpact: apiApp.local_impact || "Not provided",
      categories: audienceLabels,
    },
    venue: {
      venueName: apiApp.venue_name,
      capacity: apiApp.capacity ?? 0,
      amenities: amenitySlugs,
      location: {
        latitude: apiApp.venue_latitude
          ? Number(apiApp.venue_latitude)
          : -17.8252,
        longitude: apiApp.venue_longitude
          ? Number(apiApp.venue_longitude)
          : 31.0335,
      },
    },
  };

  const statusStyle = getStatusStyles(status);

  return (
    <Box sx={{ minHeight: "100vh" }}>
      {/* TOP BAR */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Button
          onClick={() => navigate(-1)}
          startIcon={<ArrowBackIosNewIcon sx={{ fontSize: 14 }} />}
          sx={{
            minWidth: "auto",
            px: 2,
            py: 1.1,
            borderRadius: "999px",
            bgcolor: colors.primary[800],
            color: textMuted,
            textTransform: "none",
            fontWeight: 600,
            boxShadow: "none",
            border: `1px solid ${textMuted}22`,
            "&:hover": { bgcolor: mutedCard, boxShadow: "none" },
          }}
        >
          BACK
        </Button>

        <Stack direction="row" spacing={1.5} alignItems="center">
          <Chip
            label={STATUS_LABELS[status]}
            size="small"
            sx={{
              ...statusStyle,
              fontWeight: 700,
              fontSize: 11,
              height: 28,
              borderRadius: "999px",
              "& .MuiChip-label": { px: 1.5 },
            }}
          />

          <FormControl size="small">
            <Select
              value={status}
              onChange={(e) =>
                handleStatusChange(e.target.value as ApplicationStatus)
              }
              input={<OutlinedInput notched={false} />}
              IconComponent={KeyboardArrowDownRoundedIcon}
              sx={{
                minWidth: 190,
                height: 40,
                borderRadius: "999px",
                bgcolor: colors.primary[800],
                color: textMuted,
                fontWeight: 500,
                ".MuiOutlinedInput-notchedOutline": {
                  borderColor: `${textMuted}22`,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: `${textMuted}44`,
                },
              }}
            >
              <MenuItem value="submitted" disabled>
                Pending Decision
              </MenuItem>
              <MenuItem value="approved">Approve</MenuItem>
              <MenuItem value="under_review">Request Info</MenuItem>
              <MenuItem value="rejected">Decline</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Box>

      {/* MAIN GRID */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "380px 1fr" },
          gap: 3,
          alignItems: "start",
        }}
      >
        {/* ── LEFT PANEL ── */}
        <Stack spacing={2.5}>
          {/* ORGANIZER CARD */}
          <Card
            elevation={0}
            sx={{ bgcolor: cardBg, borderRadius: "18px", overflow: "hidden" }}
          >
            <Box sx={{ p: 2.5 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  mb: 2.5,
                }}
              >
                <Avatar
                  sx={{
                    width: 52,
                    height: 52,
                    bgcolor: "#0F4D92",
                    fontSize: 17,
                    fontWeight: 700,
                  }}
                >
                  {getInitials(app.organiser.leadName)}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: 15,
                      fontWeight: 700,
                      color: textPrimary,
                      lineHeight: 1.2,
                    }}
                  >
                    {app.organiser.leadName}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.3,
                      fontSize: 13,
                      color: textMuted,
                      lineHeight: 1.2,
                    }}
                  >
                    {app.organiser.orgName}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.75}>
                  <IconButton
                    size="small"
                    sx={{
                      width: 34,
                      height: 34,
                      bgcolor: "#FFF1F222",
                      color: "#EA5B71",
                      border: "1px solid #EA5B7122",
                      "&:hover": { bgcolor: "#FFF1F244" },
                    }}
                  >
                    <EmailOutlinedIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    component="a"
                    href={`https://wa.me/${app.organiser.phoneNumber}`}
                    target="_blank"
                    sx={{
                      width: 34,
                      height: 34,
                      bgcolor: "#EEFCEF22",
                      color: "#35B66B",
                      border: "1px solid #35B66B22",
                      "&:hover": { bgcolor: "#EEFCEF44" },
                    }}
                  >
                    <WhatsAppIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() =>
                      navigator.clipboard.writeText(app.organiser.phoneNumber)
                    }
                    sx={{
                      width: 34,
                      height: 34,
                      bgcolor: mutedCard,
                      color: textMuted,
                      border: `1px solid ${textMuted}22`,
                      "&:hover": { bgcolor: colors.primary[800] },
                    }}
                  >
                    <ContentCopyIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </Stack>
              </Box>

              {/* Contact info */}
              <Box
                sx={{
                  bgcolor: mutedCard,
                  borderRadius: "12px",
                  p: 1.75,
                  mb: 2,
                }}
              >
                {[
                  { label: "Email", value: app.organiser.email },
                  { label: "Phone", value: app.organiser.phoneNumber },
                ].map((item) => (
                  <Box
                    key={item.label}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      py: 0.75,
                    }}
                  >
                    <Typography
                      sx={{ fontSize: 12, color: textMuted, fontWeight: 500 }}
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      sx={{ fontSize: 12, color: textPrimary, fontWeight: 600 }}
                    >
                      {item.value}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Notes blocks */}
              {[
                {
                  title: "Internal Notes",
                  value: internalNotes,
                  setValue: setInternalNotes,
                  editing: editInternalNotes,
                  setEditing: setEditInternalNotes,
                },
                {
                  title: "Notes",
                  value: notes,
                  setValue: setNotes,
                  editing: editNotes,
                  setEditing: setEditNotes,
                },
              ].map((item) => (
                <Box
                  key={item.title}
                  sx={{
                    bgcolor: mutedCard,
                    borderRadius: "14px",
                    p: 1.75,
                    mb: 2,
                    border: `1px solid ${textMuted}14`,
                  }}
                >
                  <Box
                    sx={{
                      mb: 1,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{ fontSize: 13, fontWeight: 700, color: textPrimary }}
                    >
                      {item.title}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => item.setEditing(!item.editing)}
                      sx={{ width: 28, height: 28, color: colors.primary[400] }}
                    >
                      {item.editing ? (
                        <CheckIcon sx={{ fontSize: 17 }} />
                      ) : (
                        <EditOutlinedIcon sx={{ fontSize: 17 }} />
                      )}
                    </IconButton>
                  </Box>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    value={item.value}
                    onChange={(e) => item.setValue(e.target.value)}
                    disabled={!item.editing}
                    placeholder={`Add ${item.title.toLowerCase()}...`}
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        p: 0,
                        bgcolor: "transparent",
                        borderRadius: 2,
                        alignItems: "flex-start",
                        "& fieldset": {
                          borderWidth: item.editing ? 1 : 0,
                          borderColor: `${textMuted}30`,
                        },
                      },
                      "& textarea": {
                        padding: item.editing ? "10px 12px" : "0px",
                        fontSize: 13,
                        lineHeight: 1.5,
                        color: textPrimary,
                      },
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Card>

          <Card elevation={0} sx={{ bgcolor: cardBg, borderRadius: "18px" }}>
            <Box sx={{ p: 2.5 }}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: textMuted,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  mb: 1.5,
                }}
              >
                Application Info
              </Typography>
              {[
                { label: "Confirmation ID", value: app.id },
                {
                  label: "Date Submitted",
                  value: new Date(app.submitted).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  }),
                },
              ].map((item) => (
                <Box
                  key={item.label}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: 0.75,
                  }}
                >
                  <Typography sx={{ fontSize: 12, color: textMuted }}>
                    {item.label}
                  </Typography>
                  <Typography
                    sx={{ fontSize: 12, color: textPrimary, fontWeight: 600 }}
                  >
                    {item.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Card>
        </Stack>

        <Stack spacing={2.5}>
          <Card elevation={0} sx={{ bgcolor: cardBg, borderRadius: "18px" }}>
            <Box sx={{ p: 2.5 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: "12px",
                    bgcolor: "#3B82F622",
                    color: "#3B82F6",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <LiveTvOutlinedIcon sx={{ fontSize: 24 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: 19,
                      fontWeight: 700,
                      color: textPrimary,
                      lineHeight: 1.25,
                    }}
                  >
                    {app.conference.title}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={0.75}
                    mt={0.75}
                    flexWrap="wrap"
                    gap={0.5}
                  >
                    {app.conference.categories.map((cat) => (
                      <Chip
                        key={cat}
                        label={cat}
                        size="small"
                        sx={{
                          fontSize: 11,
                          height: 22,
                          bgcolor: colors.primary[200] || "#D1A07722",
                          color: colors.primary[400],
                          borderRadius: "6px",
                          "& .MuiChip-label": { px: 1 },
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              </Box>

              <Divider sx={{ my: 2, borderColor: `${textMuted}18` }} />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 2,
                  mb: 2,
                }}
              >
                {[
                  {
                    icon: <CalendarIcon sx={{ fontSize: 24 }} />,
                    label: "Date",
                    value: new Date(app.conference.date).toLocaleDateString(
                      "en-GB",
                      { day: "numeric", month: "long", year: "numeric" },
                    ),
                  },
                  {
                    icon: <PeopleOutlineIcon sx={{ fontSize: 24 }} />,
                    label: "Expected Attendees",
                    value: app.conference.attendees.toLocaleString(),
                  },
                  {
                    icon: <LocationIcon sx={{ fontSize: 24 }} />,
                    label: "Venue",
                    value: app.venue.venueName,
                  },
                ].map((item) => (
                  <Box
                    key={item.label}
                    sx={{ bgcolor: mutedCard, borderRadius: "12px", p: 1.75 }}
                  >
                    <Box sx={{ color: textMuted, mb: 0.75 }}>{item.icon}</Box>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: textMuted,
                        textTransform: "uppercase",
                        letterSpacing: 0.4,
                        fontWeight: 600,
                        mb: 0.4,
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 16,
                        fontWeight: 800,
                        color: textPrimary,
                        lineHeight: 1.3,
                      }}
                    >
                      {item.value}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Theme */}
              <Box
                sx={{
                  bgcolor: mutedCard,
                  borderRadius: "12px",
                  p: 1.75,
                  mb: 2,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 10,
                    color: textMuted,
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                    fontWeight: 600,
                    mb: 0.75,
                  }}
                >
                  Event Theme
                </Typography>
                <Typography
                  sx={{ fontSize: 14, color: textPrimary, lineHeight: 1.6 }}
                >
                  {app.conference.theme}
                </Typography>
              </Box>

              {/* Local Impact */}
              <Box sx={{ bgcolor: mutedCard, borderRadius: "12px", p: 1.75 }}>
                <Typography
                  sx={{
                    fontSize: 10,
                    color: textMuted,
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                    fontWeight: 600,
                    mb: 0.75,
                  }}
                >
                  Broadcast Impact
                </Typography>
                <Typography
                  sx={{ fontSize: 14, color: textPrimary, lineHeight: 1.6 }}
                >
                  {app.conference.localImpact}
                </Typography>
              </Box>
            </Box>
          </Card>

          <Card elevation={0} sx={{ bgcolor: cardBg, borderRadius: "18px" }}>
            <Box sx={{ p: 2.5 }}>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: textPrimary,
                  mb: 2,
                }}
              >
                Venue & Broadcast Infrastructure
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 2,
                }}
              >
                {/* Venue details */}
                <Box>
                  {[
                    { label: "Venue Name", value: app.venue.venueName },
                    {
                      label: "In-Person Capacity",
                      value: app.venue.capacity.toLocaleString(),
                    },
                    {
                      label: "Coordinates",
                      value: `${app.venue.location.latitude}, ${app.venue.location.longitude}`,
                    },
                  ].map((item) => (
                    <Box
                      key={item.label}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 0.9,
                        borderBottom: `1px solid ${textMuted}12`,
                      }}
                    >
                      <Typography sx={{ fontSize: 12, color: textMuted }}>
                        {item.label}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: textPrimary,
                          fontWeight: 600,
                        }}
                      >
                        {item.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* Amenities */}
                <Box>
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: textMuted,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                      mb: 1.25,
                    }}
                  >
                    Confirmed Amenities
                  </Typography>
                  <Stack spacing={1}>
                    {app.venue.amenities.map((id) => {
                      const amenity = amenityLabels[id];
                      if (!amenity) return null;
                      return (
                        <Box
                          key={id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.25,
                            bgcolor: "#4BA66314",
                            borderRadius: "10px",
                            px: 1.5,
                            py: 1,
                          }}
                        >
                          <Box sx={{ color: "#4BA663" }}>{amenity.icon}</Box>
                          <Typography
                            sx={{
                              fontSize: 13,
                              color: textPrimary,
                              fontWeight: 500,
                            }}
                          >
                            {amenity.label}
                          </Typography>
                          <CheckIcon
                            sx={{ fontSize: 14, color: "#4BA663", ml: "auto" }}
                          />
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              </Box>

              <Box
                sx={{
                  mt: 2.5,
                  borderRadius: "14px",
                  overflow: "hidden",
                  height: 180,
                  bgcolor: mutedCard,
                  border: `1px solid ${textMuted}18`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                }}
              >
                <iframe
                  title="venue-map"
                  src={`https://www.google.com/maps?q=${app.venue.location.latitude},${app.venue.location.longitude}&z=15&output=embed`}
                  width="100%"
                  height="100%"
                  style={{ border: 0, display: "block" }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </Box>
            </Box>
          </Card>

          <Card elevation={0} sx={{ bgcolor: cardBg, borderRadius: "18px" }}>
            <Box sx={{ p: 2.5 }}>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: textPrimary,
                  mb: 1.5,
                }}
              >
                Organiser Experience
              </Typography>
              <Typography
                sx={{ fontSize: 14, color: textMuted, lineHeight: 1.7 }}
              >
                {app.organiser.experience}
              </Typography>
            </Box>
          </Card>
        </Stack>
      </Box>

      {/* ACTION MODAL */}
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent sx={{ p: 3, bgcolor: cardBg }}>
          {selectedAction && selectedAction !== "submitted" && selectedAction !== "draft" && (
            <>
              <Typography
                sx={{
                  fontSize: 22,
                  fontWeight: 700,
                  color: textPrimary,
                  mb: 0.75,
                }}
              >
                {modalContent[selectedAction].title}
              </Typography>
              <Typography sx={{ color: textMuted, mb: 3, fontSize: 14 }}>
                {modalContent[selectedAction].subtitle}
              </Typography>
            </>
          )}

          {selectedAction === "rejected" && (
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  mb: 0.75,
                  fontSize: 12,
                  fontWeight: 600,
                  color: textMuted,
                }}
              >
                Reason
              </Typography>
              <Select
                fullWidth
                value={reason}
                onChange={(e) => {
                  const selected = declineReasons.find(
                    (x) => x.label === e.target.value,
                  );
                  setReason(e.target.value);
                  if (selected) setMessage(selected.message);
                }}
                sx={{
                  height: 40,
                  borderRadius: "8px",
                  fontSize: 13,
                  color: textPrimary,
                  bgcolor: mutedCard,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: `${textMuted}30`,
                  },
                }}
              >
                {declineReasons.map((r) => (
                  <MenuItem key={r.label} value={r.label} sx={{ fontSize: 13 }}>
                    {r.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          )}

          <Box>
            <Typography
              sx={{ mb: 0.75, fontSize: 12, fontWeight: 600, color: textMuted }}
            >
              Message
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Write the WhatsApp message to send to the applicant"
              sx={{
                "& .MuiOutlinedInput-root": {
                  alignItems: "flex-start",
                  borderRadius: "8px",
                  bgcolor: mutedCard,
                  fontSize: 13,
                  color: textPrimary,
                  "& fieldset": { borderColor: `${textMuted}30` },
                  "&:hover fieldset": { borderColor: `${textMuted}55` },
                },
                "& .MuiOutlinedInput-input": { padding: "12px", fontSize: 13 },
              }}
            />
          </Box>

          <Box display="flex" justifyContent="flex-end" gap={1.5} mt={3}>
            <Button
              onClick={() => setOpenModal(false)}
              sx={{ color: textMuted, textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={savingStatus}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                fontWeight: 600,
                bgcolor:
                  selectedAction === "approved"
                    ? "#4BA663"
                    : selectedAction === "rejected"
                      ? "#EB4335"
                      : "#3B82F6",
                "&:hover": { opacity: 0.9 },
              }}
            >
              {savingStatus
                ? "Saving…"
                : selectedAction === "rejected"
                  ? "Confirm Decline"
                  : selectedAction === "approved"
                    ? "Confirm Approval"
                    : "Send Request"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
